import { User, RegisterFormData, LoginFormData } from '../types/user';
import { hashPassword } from './encryption';
import { getUsers, saveUsers, setCurrentUser, clearCurrentUser } from './localDatabase';
import { addAuditLog } from './auditLog';
import { createOrganization } from './organizationStorage';
import { supabase, isOfflineModeEnabled, forceOfflineMode } from './supabase';

/**
 * Generate secure random password
 */
const generateSecurePassword = (): string => {
  const length = 12;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  
  // Ensure at least one of each required character type
  password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
  password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
  password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
  password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

/**
 * Generate credentials for a new technician
 */
export const generateTechnicianCredentials = async (email: string) => {
  const users = getUsers();
  
  // Check if email is already in use
  if (users.some(u => u.email === email)) {
    throw new Error('Diese E-Mail-Adresse wird bereits verwendet');
  }
  
  // Get current user for organization ID
  const currentUser = getCurrentUser();
  if (!currentUser?.organizationId) {
    throw new Error('Keine Organisation ausgewählt');
  }
  
  // Generate password
  const password = generateSecurePassword();
  
  // Create user account
  const newUser: User = {
    id: crypto.randomUUID(),
    email,
    password: hashPassword(password),
    name: email.split('@')[0], // Use part before @ as initial name
    role: 'technician',
    organizationId: currentUser.organizationId,
    lastLogin: undefined,
    permissions: {
      canCreateBuildings: true,
      canEditBuildings: true,
      canDeleteBuildings: false,
      canManageTechnicians: false,
      canManageKonzernIds: false,
      canExportData: true,
      canImportData: false,
      canAccessApi: false,
      canViewAuditLogs: false,
      canManagePermissions: false
    }
  };
  
  // Save user
  users.push(newUser);
  saveUsers(users);
  
  // Log the creation
  addAuditLog('create', 'user', `Technician account created: ${email}`, newUser.id);
  
  // Return credentials
  return {
    email,
    password
  };
};

export const login = async (data: LoginFormData): Promise<User> => {
  try {
    // For demo purposes, allow login with the default admin credentials without hashing
    if (data.email === 'admin@example.com' && data.password === 'Admin123') {
      const users = getUsers();
      const adminUser = users.find(u => u.email === 'admin@example.com');
      
      if (adminUser) {
        const { password: _, ...userWithoutPassword } = adminUser;
        const updatedUser = { 
          ...userWithoutPassword, 
          lastLogin: new Date() 
        };
        
        const updatedUsers = users.map(u => 
          u.id === adminUser.id ? { ...u, lastLogin: updatedUser.lastLogin } : u
        );
        saveUsers(updatedUsers);
        setCurrentUser(updatedUser);
        
        // Log the login
        addAuditLog('login', 'user', `User logged in: ${adminUser.email}`, adminUser.id);
        
        return updatedUser;
      }
    }
    
    // Check if we're in offline mode
    const offlineMode = isOfflineModeEnabled();
    
    // If we're in online mode, try to login with Supabase first
    if (!offlineMode) {
      try {
        console.log('Attempting Supabase login for:', data.email);
        const { data: authData, error } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password
        });
        
        if (error) {
          console.error('Supabase login error:', error);
          
          // If we get an invalid credentials error, try local login
          if (error.message === 'Invalid login credentials') {
            console.log('Invalid credentials, trying local login');
          } else {
            // For other errors, force offline mode and try local login
            console.log('Other Supabase error, forcing offline mode');
            forceOfflineMode();
          }
          
          // Fall through to local login
        } else if (authData.user) {
          console.log('Supabase login successful for:', authData.user.email);
          
          // Get user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', authData.user.id)
            .single();
            
          // Create user object
          const userObj: User = {
            id: authData.user.id,
            email: authData.user.email || '',
            name: profile?.name || authData.user.user_metadata?.name || 'Unnamed User',
            company: profile?.company || authData.user.user_metadata?.company,
            role: profile?.role as any || 'technician',
            lastLogin: new Date(),
            organizationId: profile?.organization_id,
            permissions: profile?.permissions as any || {
              canCreateBuildings: true,
              canEditBuildings: true,
              canDeleteBuildings: false,
              canManageTechnicians: false,
              canManageKonzernIds: false,
              canExportData: true,
              canImportData: true,
              canAccessApi: false,
              canViewAuditLogs: false,
              canManagePermissions: false
            }
          };
          
          // Save user to local storage
          setCurrentUser(userObj);
          
          // Log the login
          addAuditLog('login', 'user', `User logged in: ${userObj.email}`, userObj.id);
          
          return userObj;
        }
      } catch (error) {
        console.warn('Supabase login failed, falling back to local login:', error);
        // Force offline mode and fall through to local login
        forceOfflineMode();
      }
    }
    
    // Local login flow with password hashing
    console.log('Attempting local login for:', data.email);
    const users = getUsers();
    const hashedPassword = hashPassword(data.password);
    const user = users.find(u => u.email === data.email && u.password === hashedPassword);
    
    if (!user) {
      throw new Error('Ungültige E-Mail-Adresse oder Passwort');
    }

    // Ensure user has permissions
    if (!user.permissions) {
      user.permissions = user.role === 'admin' ? {
        // Admin permissions
        canCreateBuildings: true,
        canEditBuildings: true,
        canDeleteBuildings: true,
        canManageTechnicians: true,
        canManageKonzernIds: true,
        canExportData: true,
        canImportData: true,
        canAccessApi: true,
        canViewAuditLogs: true,
        canManagePermissions: true
      } : {
        // Default technician permissions
        canCreateBuildings: true,
        canEditBuildings: true,
        canDeleteBuildings: false,
        canManageTechnicians: false,
        canManageKonzernIds: false,
        canExportData: true,
        canImportData: true,
        canAccessApi: false,
        canViewAuditLogs: false,
        canManagePermissions: false
      };
    }

    const { password: _, ...userWithoutPassword } = user;
    const updatedUser = { 
      ...userWithoutPassword, 
      lastLogin: new Date() 
    };
    
    const updatedUsers = users.map(u => 
      u.id === user.id ? { ...u, lastLogin: updatedUser.lastLogin, permissions: user.permissions } : u
    );
    saveUsers(updatedUsers);
    setCurrentUser(updatedUser);
    
    // Log the login
    addAuditLog('login', 'user', `User logged in: ${user.email}`, user.id);
    
    console.log('Local login successful for:', user.email);
    return updatedUser;
  } catch (error) {
    console.error('Error logging in user:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Anmeldung fehlgeschlagen');
  }
};

export const register = async (data: RegisterFormData): Promise<User> => {
  try {
    // Check if email is already in use locally
    const users = getUsers();
    if (users.some(u => u.email === data.email)) {
      throw new Error('Diese E-Mail-Adresse wird bereits verwendet');
    }

    const userId = crypto.randomUUID();
    let organizationId: string;

    // Create organization if name is provided
    if (data.organizationName) {
      const organization = createOrganization(
        data.organizationName,
        userId,
        data.organizationPlan || 'free'
      );
      organizationId = organization.id;
    } else {
      throw new Error('Organisationsname ist erforderlich');
    }

    // If we're in online mode, try to register with Supabase
    if (!isOfflineModeEnabled()) {
      try {
        console.log('Attempting Supabase registration for:', data.email);
        const { data: authData, error } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              name: data.name,
              company: data.company,
              organizationName: data.organizationName,
              organizationPlan: data.organizationPlan || 'free'
            }
          }
        });
        
        if (error) {
          console.error('Supabase registration error:', error);
          
          // Check for password security errors
          if (error.message.includes('password')) {
            if (error.message.includes('leaked password')) {
              throw new Error('Dieses Passwort wurde in einem Datenleck gefunden. Bitte wählen Sie ein sichereres Passwort.');
            } else if (error.message.includes('minimum password length')) {
              throw new Error('Passwort muss mindestens 8 Zeichen lang sein.');
            } else {
              throw new Error(`Passwort-Fehler: ${error.message}`);
            }
          }
          
          // Force offline mode and fall through to local registration
          forceOfflineMode();
        } else if (authData.user) {
          console.log('Supabase registration successful for:', data.email);
          
          // Create user object
          const userObj: User = {
            id: authData.user.id,
            email: data.email,
            name: data.name,
            company: data.company,
            role: data.role || 'admin',
            lastLogin: new Date(),
            organizationId,
            permissions: data.role === 'admin' ? {
              canCreateBuildings: true,
              canEditBuildings: true,
              canDeleteBuildings: true,
              canManageTechnicians: true,
              canManageKonzernIds: true,
              canExportData: true,
              canImportData: true,
              canAccessApi: true,
              canViewAuditLogs: true,
              canManagePermissions: true
            } : {
              canCreateBuildings: true,
              canEditBuildings: true,
              canDeleteBuildings: false,
              canManageTechnicians: false,
              canManageKonzernIds: false,
              canExportData: true,
              canImportData: true,
              canAccessApi: false,
              canViewAuditLogs: false,
              canManagePermissions: false
            }
          };
          
          // Save user to local storage
          setCurrentUser(userObj);
          
          // Log the registration
          addAuditLog('create', 'user', `New user registered: ${userObj.email}`, userObj.id);
          
          return userObj;
        }
      } catch (error) {
        console.warn('Supabase registration failed, falling back to local registration:', error);
        // If it's a password security error, rethrow it
        if (error instanceof Error && 
            (error.message.includes('Passwort') || 
             error.message.includes('password'))) {
          throw error;
        }
        // Otherwise, force offline mode and fall through to local registration
        forceOfflineMode();
      }
    }

    // Local registration
    console.log('Performing local registration for:', data.email);
    const newUser: User = {
      id: userId,
      email: data.email,
      password: hashPassword(data.password),
      name: data.name,
      role: data.role || 'admin',
      company: data.company,
      organizationId,
      lastLogin: new Date(),
      permissions: data.role === 'admin' ? {
        // Admin permissions
        canCreateBuildings: true,
        canEditBuildings: true,
        canDeleteBuildings: true,
        canManageTechnicians: true,
        canManageKonzernIds: true,
        canExportData: true,
        canImportData: true,
        canAccessApi: true,
        canViewAuditLogs: true,
        canManagePermissions: true
      } : {
        // Default technician permissions
        canCreateBuildings: true,
        canEditBuildings: true,
        canDeleteBuildings: false,
        canManageTechnicians: false,
        canManageKonzernIds: false,
        canExportData: true,
        canImportData: true,
        canAccessApi: false,
        canViewAuditLogs: false,
        canManagePermissions: false
      }
    };

    users.push(newUser);
    saveUsers(users);
    
    const { password: _, ...userWithoutPassword } = newUser;
    setCurrentUser(userWithoutPassword);
    
    // Log the registration
    addAuditLog('create', 'user', `New user registered: ${newUser.email}`, newUser.id);
    
    console.log('Local registration successful for:', data.email);
    return userWithoutPassword;
  } catch (error) {
    console.error('Error registering user:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Registrierung fehlgeschlagen');
  }
};

export const signOut = async (): Promise<void> => {
  try {
    // Check if we're in offline mode
    const offlineMode = isOfflineModeEnabled();
    
    // If we're in online mode, try to sign out from Supabase
    if (!offlineMode) {
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.warn('Supabase sign out failed:', error);
        // Continue with local sign out
      }
    }
    
    // Clear local user
    clearCurrentUser();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

// Helper function to get the current user
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('installtrack_current_user');
    if (!userStr) return null;
    
    return JSON.parse(userStr, (key, value) => {
      if (key === 'lastLogin' && value) {
        return new Date(value);
      }
      return value;
    });
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateProfile = async (updates: Partial<User>): Promise<boolean> => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No user is logged in');
    }
    
    // Update local user
    const updatedUser = {
      ...currentUser,
      ...updates
    };
    
    setCurrentUser(updatedUser);
    
    // If we're in online mode, update Supabase profile
    if (!isOfflineModeEnabled()) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            name: updates.name,
            company: updates.company
          })
          .eq('id', currentUser.id);
          
        if (error) {
          console.error('Error updating Supabase profile:', error);
          // Continue with local update
        }
      } catch (error) {
        console.warn('Supabase profile update failed:', error);
        // Continue with local update
      }
    }
    
    // Update user in users list
    const users = getUsers();
    const userIndex = users.findIndex(u => u.id === currentUser.id);
    
    if (userIndex !== -1) {
      users[userIndex] = {
        ...users[userIndex],
        ...updates
      };
      saveUsers(users);
    }
    
    // Log the update
    addAuditLog('update', 'user', `User profile updated: ${currentUser.email}`, currentUser.id);
    
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};